import { isNull, omitBy } from 'lodash-es';
import { normalize } from 'normalizr';

import type { EndpointResponse } from '@iconik-io/iconik-api/src/client/response';
import {
  type AssetsEditSegmentSchemaPatched,
  type AssetsHttpGetAssetsByAssetIdSegmentsQueryPatched,
  getAssetSegmentsRequest,
} from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import {
  type AssetsEditSegmentSchemaPartial,
  type AssetsSegmentElasticSchemaWithIdNullable,
  httpDeleteAssetsByAssetIdSegmentsBySegmentId,
  httpPatchAssetsByAssetIdSegmentsBySegmentId,
  httpPostAssetsByAssetIdSegments,
} from '@iconik-io/iconik-api/src/services/assets';
import { createEntityDenormalizer } from '@iconik-io/shared-lib/src/redux/entities/normalizers';
import { entitySchemas } from '@iconik-io/shared-lib/src/redux/entities/schemas';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';

import type { AppDispatch, GetState } from '../../store/types';
import {
  getMetadataValuesForObject,
  putAssetSegmentMetadataValues,
} from '../metadata_values/thunks';
import {
  assetTimeBasedMetadataLoadingUpdated,
  assetTimeBasedMetadataReceived,
  filteredIdsReceived,
} from './slice';

const defaultParams = {
  segment_type: ['TAG', 'GENERIC'],
  sort: 'time_start_milliseconds,asc',
  per_page: 100,
  page: 1,
};

export const updateSegment =
  (assetId: string, segmentId: string, data: AssetsEditSegmentSchemaPartial) =>
  (dispatch: AppDispatch) =>
    dispatch(
      httpPatchAssetsByAssetIdSegmentsBySegmentId(
        assetId,
        segmentId,
        omitBy(data, isNull),
        {
          index_immediately: 'true',
        },
      ),
    );

export const updateStateSegments =
  (
    assetId: string,
    segments: Partial<AssetsSegmentElasticSchemaWithIdNullable>[],
  ) =>
  (dispatch: AppDispatch): void => {
    const { entities, result } = normalize(segments, [entitySchemas.segments]);
    dispatch(updateEntities(entities));
    dispatch(assetTimeBasedMetadataReceived({ assetId, segmentIds: result }));
  };

export const getTimebasedMetadata =
  (assetId: string, params: AssetsHttpGetAssetsByAssetIdSegmentsQueryPatched) =>
  (dispatch: AppDispatch): void => {
    const requestParams: AssetsHttpGetAssetsByAssetIdSegmentsQueryPatched = {
      ...defaultParams,
      ...params,
    };

    let segments: AssetsSegmentElasticSchemaWithIdNullable[] = [];

    // Request comments segments in the loop
    const loopFetch = (
      response: EndpointResponse<ReturnType<typeof getAssetSegmentsRequest>>,
    ): Promise<AssetsSegmentElasticSchemaWithIdNullable[]> => {
      const { objects, page, pages } = response.body;
      if (objects) {
        segments = segments.concat(objects);
      }
      if (pages && page && pages > page) {
        return dispatch(
          getAssetSegmentsRequest(assetId, {
            ...requestParams,
            page: page + 1,
          }),
        ).then((nextResponse) => loopFetch(nextResponse));
      }

      return Promise.resolve(segments);
    };

    dispatch(assetTimeBasedMetadataLoadingUpdated({ assetId, loading: true }));

    // Recursively get all pages from the comments
    dispatch(getAssetSegmentsRequest(assetId, requestParams))
      .then((response) => loopFetch(response))
      .then((allSegments) => {
        dispatch(updateStateSegments(assetId, allSegments));
        dispatch(
          assetTimeBasedMetadataLoadingUpdated({ assetId, loading: false }),
        );
      });
  };

export const createMetadataSegment =
  (
    assetId: string,
    segment: AssetsEditSegmentSchemaPatched,
    metadataViewId: string,
  ) =>
  async (dispatch: AppDispatch) => {
    const { metadata, ...segmentData } = segment;
    const { body } = await dispatch(
      httpPostAssetsByAssetIdSegments(assetId, segmentData),
    );
    if (body.id && metadata) {
      dispatch(
        putAssetSegmentMetadataValues(
          assetId,
          body.id,
          metadataViewId,
          metadata,
        ),
      );
      dispatch(
        updateStateSegments(assetId, [
          {
            ...body,
            metadata,
          },
        ]),
      );
    }
    return body;
  };

export const deleteSegment =
  (assetId: string, segmentId: string) =>
  (dispatch: AppDispatch, getState: GetState): Promise<unknown> =>
    dispatch(
      httpDeleteAssetsByAssetIdSegmentsBySegmentId(assetId, segmentId),
    ).then(() => {
      const { entities } = normalize(
        [
          {
            id: segmentId,
            deleted: true,
            status: 'DELETED',
          },
        ],
        [entitySchemas.segments],
      );

      const subclipId = getState().entities.segments[segmentId].subclip_id;
      const subclipEntityItems = Object.fromEntries([
        [subclipId, { status: 'DELETED' }],
      ]);

      dispatch(
        updateEntities({
          search_items: subclipEntityItems,
          assets: subclipEntityItems,
          segments: entities.segments,
        }),
      );
    });

export const searchTimebasedMetadata =
  (assetId: string, _versionId: string | undefined, query: string) =>
  (dispatch: AppDispatch): void => {
    const params = {
      ...defaultParams,
      query,
    };
    if (query) {
      dispatch(
        assetTimeBasedMetadataLoadingUpdated({ assetId, loading: true }),
      );

      // Need to fix race condition here..
      dispatch(getAssetSegmentsRequest(assetId, params)).then(({ body }) => {
        dispatch(
          assetTimeBasedMetadataLoadingUpdated({ assetId, loading: false }),
        );
        if (body.objects) {
          dispatch(
            filteredIdsReceived({
              assetId,
              filteredIds: body.objects.map((segment) => segment.id),
            }),
          );
        }
      });
    } else {
      dispatch(filteredIdsReceived({ assetId, filteredIds: undefined }));
    }
  };

const denormalizeSegment = createEntityDenormalizer('segments');

export const addMetadataValuesForTags =
  (assetId: string, segmentId: string, viewId: string) =>
  (dispatch: AppDispatch, getState: GetState): void => {
    // elastic return no score for _gcvi_tags
    // when edit a segment we request metadata values and update the data we get from elastic
    dispatch(
      getMetadataValuesForObject(assetId, 'segments', segmentId, viewId),
    ).then(() => {
      const segment = denormalizeSegment(getState().entities, segmentId);

      if (segment) {
        const values =
          getState().metadataValuesReducer.metadataValues[segmentId];

        if (values && values._gcvi_tags) {
          const updatedSegment = {
            ...segment,
            metadata: { ...values },
          };

          dispatch(updateStateSegments(assetId, [updatedSegment]));
        }
      }
    });
  };
